import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Plus, Trash2, RefreshCw, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import * as crmBackend from '../services/crmBackendService';
import { stripHtml } from '../lib/utils';
import { loadDraft, saveDraft, clearDraft, hasContent, isPending, newClientId } from '../lib/unsavedDraft';
import { enqueue, runOp, errMessage } from '../lib/itemSync';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const SavedBadge = ({ row, onRetry }) => {
  if (row._sync === 'error') {
    return (
      <span className="text-[10px] font-semibold text-red-600 flex items-center gap-1" title={row._err}>
        Not saved
        <button type="button" onClick={onRetry} className="underline">Retry</button>
      </span>
    );
  }
  if (row._sync === 'saved') return <span className="text-[10px] font-semibold text-green-700">Saved ✓</span>;
  return <span className="text-[10px] font-semibold text-amber-600">Saving…</span>;
};

/**
 * Full-featured Estimate line-item editor, embedded directly in the admin
 * console's Estimate Detail dialog (Dashboard.jsx / ContactDetail.jsx) —
 * NOT a cut-down version. Pulls the CURRENT server items fresh on open
 * (never the stale copy already sitting in the parent's dialog state), and
 * every add/edit/remove is written to ERP immediately through the exact
 * same per-job serialized save-verify-retry queue the tech app's
 * TechAddItems.jsx uses (lib/itemSync.js's enqueue/runOp/fetchServer) — not
 * a reimplementation of that mechanism.
 *
 * No admin-specific OR tech-specific business restrictions are applied here:
 * no requirement for zone tags, no job-status gate, no "assigned tech"
 * filter. The ONLY thing that blocks a save is the same thing that blocks it
 * for a tech — runOp()/fetchServer() re-checking the live doc's approval-
 * status edit lock / submitted-doc lock (events/quotation.py before_save's
 * locked_statuses check) right before every write. That is core ERP data
 * integrity protection, not a tech-app convenience limit, so it intentionally
 * stays for admin too. Photo capture is out of scope here (line items only);
 * existing before/after photos on a row, if any, are left untouched by this
 * editor's PUT (toPutRow round-trips whatever URLs are already on the row).
 *
 * Props:
 *   jobId       — Quotation name (estimate)
 *   locked      — best-effort client hint from the already-loaded doc
 *                 (docstatus===1 || custom_signature_captured || approval
 *                 Approved) so the UI can show "locked" immediately without
 *                 waiting on a fetch; the real enforcement is server-side.
 *   onChanged   — called (rows) after any successful save so the parent
 *                 dialog can refresh totals/status from the server.
 */
export default function EstimateItemsEditor({ jobId, locked: lockedHint, onChanged }) {
  const itemsRef = useRef([]);
  const timersRef = useRef({});
  const loadFailedRef = useRef(true);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [locked, setLocked] = useState(!!lockedHint);
  const [restoredCount, setRestoredCount] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [zone, setZone] = useState('');
  const [rate, setRate] = useState('');
  const [qty, setQty] = useState('1');
  const [description, setDescription] = useState('');

  const persist = () => {
    if (loadFailedRef.current) return;
    const d = { unsavedItems: itemsRef.current.filter(isPending) };
    if (!hasContent(d)) { clearDraft(jobId); return; }
    saveDraft(jobId, d);
  };
  const updateItems = (fn) => {
    const next = fn(itemsRef.current);
    itemsRef.current = next;
    setItems(next);
    persist();
  };
  const patchRow = (cid, patch) => updateItems(rows => rows.map(r => (r._cid === cid ? { ...r, ...patch } : r)));
  const getRow = (cid) => itemsRef.current.find(r => r._cid === cid);

  const syncRow = (cid) => {
    clearTimeout(timersRef.current[cid]);
    patchRow(cid, { _sync: 'saving', _err: '' });
    let ver;
    return enqueue(jobId, async () => {
      const row = getRow(cid);
      if (!row) return { skipped: true };
      let op;
      if (row._removed) op = row.name ? 'remove' : null;
      else op = row.name ? 'edit' : 'add';
      if (!op) return { skipped: true, drop: true };
      if (op === 'edit' && row._savedVer === row._ver) return { skipped: true, done: true };
      ver = row._ver;
      const res = await runOp({ api: crmBackend, jobId, op, stripHtml, getRow: () => getRow(cid) });
      return { ...res, op };
    }).then(res => {
      if (res.drop || (res.op === 'remove' && !res.skipped)) { updateItems(rows => rows.filter(r => r._cid !== cid)); onChanged?.(res.rows); return; }
      if (res.skipped && !res.done) return;
      const row = getRow(cid);
      if (!row) return;
      const stale = ver !== undefined && row._ver !== ver;
      patchRow(cid, {
        ...(res.row?.name ? { name: res.row.name } : {}),
        _savedVer: ver !== undefined ? ver : row._savedVer,
        _sync: stale ? 'pending' : 'saved', _err: '',
      });
      if (Array.isArray(res.rows) && res.rows.length) {
        const pos = new Map(res.rows.map((r, i) => [r.name, i]));
        updateItems(rows => [
          ...rows.filter(r => r.name && pos.has(r.name)).sort((a, b) => pos.get(a.name) - pos.get(b.name)),
          ...rows.filter(r => !(r.name && pos.has(r.name))),
        ]);
      }
      onChanged?.(res.rows);
    }).catch(err => {
      const msg = errMessage(err);
      patchRow(cid, { _sync: 'error', _err: msg });
      if (/locked/i.test(msg)) setLocked(true);
    });
  };

  const editRow = (cid, patch, immediate = false) => {
    updateItems(rows => rows.map(r => {
      if (r._cid !== cid) return r;
      const n = { ...r, ...patch, _ver: (r._ver || 0) + 1, _sync: 'pending', _err: '' };
      n.amount = (Number(n.qty) || 0) * (Number(n.rate) || 0);
      return n;
    }));
    clearTimeout(timersRef.current[cid]);
    if (immediate) syncRow(cid);
    else timersRef.current[cid] = setTimeout(() => syncRow(cid), 700);
  };

  const retryAll = () => itemsRef.current.filter(r => r._sync === 'error').forEach(r => syncRow(r._cid));

  useEffect(() => {
    let cancelled = false;
    loadFailedRef.current = true;
    setLoading(true);
    setLoadError('');
    crmBackend.getEstimateDetail(jobId)
      .then(result => {
        if (cancelled) return;
        const doc = result.data || {};
        setLocked(doc.docstatus === 1 || doc.custom_signature_captured === 1 || doc.custom_approval_status === 'Approved');
        let rows = (doc.items || []).map(i => ({
          _cid: `s${i.name}`, _sync: 'saved', _ver: 0, _savedVer: 0,
          name: i.name, item_code: i.item_code, item_name: i.item_name, qty: i.qty, rate: i.rate, amount: i.amount,
          description: stripHtml(i.description),
          custom_zone: i.custom_zone || '',
          custom_before_photo_url: i.custom_before_photo_url || '',
          custom_after_photo_url: i.custom_after_photo_url || '',
        }));
        // Re-apply any pending work from a previous save attempt (shared draft
        // key with the tech app's Add Items page for the same estimate).
        const draft = loadDraft(jobId);
        const pendingDraft = (draft?.unsavedItems || []).map(u => ({ ...u, _cid: u._cid || newClientId() }));
        const toResync = [];
        pendingDraft.forEach(d => {
          if (d.name) {
            const idx = rows.findIndex(r => r.name === d.name);
            if (idx < 0) return;
            rows[idx] = { ...rows[idx], ...d, _sync: 'pending', _err: '', _ver: 1, _savedVer: 0 };
            toResync.push(rows[idx]._cid);
          } else {
            rows.push({ ...d, _sync: 'pending', _err: '', _ver: 1, _savedVer: 0 });
            toResync.push(d._cid);
          }
        });
        itemsRef.current = rows;
        setItems(rows);
        loadFailedRef.current = false;
        setRestoredCount(toResync.length);
        toResync.forEach(cid => syncRow(cid));
      })
      .catch(err => { if (!cancelled) setLoadError(errMessage(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    const q = searchQuery.trim();
    if (q.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API_BASE}/api/resource/Item`, {
          params: {
            fields: JSON.stringify(['name', 'item_name', 'item_group', 'stock_uom', 'description']),
            filters: JSON.stringify([['disabled', '=', 0], ['item_name', 'like', `%${q}%`]]),
            limit_page_length: 15,
            order_by: 'item_name asc',
          },
        });
        setSearchResults(data?.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const selectItem = async (item) => {
    setSelectedItem(item);
    setZone('');
    setRate('');
    setQty('1');
    setDescription(stripHtml(item.description));
    setSearchQuery('');
    setSearchResults([]);
    try {
      const { data } = await axios.get(`${API_BASE}/api/resource/Item Price`, {
        params: {
          fields: JSON.stringify(['price_list_rate']),
          filters: JSON.stringify([['item_code', '=', item.name], ['selling', '=', 1]]),
          order_by: 'price_list_rate desc',
          limit_page_length: 1,
        },
      });
      const priceEntry = data?.data?.[0];
      if (priceEntry?.price_list_rate) setRate(String(priceEntry.price_list_rate));
    } catch { /* leave rate empty — enter manually */ }
  };

  const addItem = () => {
    if (!selectedItem) return;
    const rateVal = parseFloat(rate) || 0;
    const qtyVal = parseFloat(qty) || 1;
    const cid = newClientId();
    updateItems(prev => [...prev, {
      _cid: cid, _sync: 'pending', _ver: 1, _savedVer: 0,
      item_code: selectedItem.name,
      item_name: selectedItem.item_name,
      qty: qtyVal,
      rate: rateVal,
      amount: rateVal * qtyVal,
      description,
      custom_zone: zone,
      custom_before_photo_url: '',
      custom_after_photo_url: '',
    }]);
    setSelectedItem(null);
    setZone('');
    setRate('');
    setQty('1');
    setDescription('');
    syncRow(cid);
  };

  const removeItem = (cid) => {
    clearTimeout(timersRef.current[cid]);
    updateItems(prev => prev.map(r => (r._cid === cid ? { ...r, _removed: true, _sync: 'pending', _err: '' } : r)));
    syncRow(cid);
  };

  const visible = items.filter(r => !r._removed);
  const total = visible.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const errorCount = items.filter(r => r._sync === 'error').length;
  const unsavedCount = items.filter(r => r._sync !== 'saved').length;

  if (loading) return <p className="text-xs text-gray-400 py-2">Loading current items…</p>;
  if (loadError) return <p className="text-xs text-red-600 py-2">Could not load items: {loadError}</p>;

  return (
    <div data-testid="estimate-items-editor" className="space-y-3">
      {restoredCount > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-2 text-xs text-blue-900">
          Restored {restoredCount} unsaved change(s) from this device — saving now.
        </div>
      )}
      {locked && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-xs font-semibold text-amber-800">
          This estimate is approved/signed or submitted — items are locked by ERP.
        </div>
      )}

      <div className="space-y-1">
        {visible.length === 0 && <p className="text-xs text-gray-400">No items yet</p>}
        {visible.map((item) => (
          <div key={item._cid} data-testid="admin-item-row" className="flex flex-col py-2 border-b last:border-0 gap-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold text-gray-900 text-sm">{item.item_name || item.item_code}</p>
                <SavedBadge row={item} onRetry={() => syncRow(item._cid)} />
              </div>
              {!locked && (
                <button onClick={() => removeItem(item._cid)} aria-label="Remove item" className="text-red-400 hover:text-red-600 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            {item._sync === 'error' && <p className="text-[11px] text-red-600">{item._err}</p>}
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] font-medium text-gray-500 uppercase">Zone</label>
                <Input type="text" placeholder="Zone" value={item.custom_zone || ''} disabled={locked}
                  onChange={(e) => editRow(item._cid, { custom_zone: e.target.value })} className="h-8 text-xs bg-white" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-gray-500 uppercase">Qty</label>
                <Input type="number" min="0.01" step="any" value={item.qty} disabled={locked}
                  onChange={(e) => editRow(item._cid, { qty: parseFloat(e.target.value) || 0 })} className="h-8 text-xs bg-white" />
              </div>
              <div>
                <label className="text-[10px] font-medium text-gray-500 uppercase">Rate ($)</label>
                <Input type="number" min="0" step="0.01" value={item.rate} disabled={locked}
                  onChange={(e) => editRow(item._cid, { rate: parseFloat(e.target.value) || 0 })} className="h-8 text-xs bg-white" />
              </div>
            </div>
            <div className="flex items-start justify-between gap-3">
              <textarea value={item.description} disabled={locked}
                onChange={(e) => editRow(item._cid, { description: e.target.value })}
                placeholder="Description (shown on estimate/invoice)" rows={2}
                className="flex-1 text-xs border rounded-md p-1.5 text-gray-700 resize-none focus:outline-none focus:ring-1 focus:ring-blue-500" />
              <p className="text-xs font-bold text-gray-900 shrink-0 mt-1">${Number(item.amount).toFixed(2)}</p>
            </div>
          </div>
        ))}
        {visible.length > 0 && <p className="text-sm font-bold text-gray-900 text-right pt-1">Total: ${total.toFixed(2)}</p>}
      </div>

      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold">
          {errorCount > 0 ? (
            <span className="text-red-600">{errorCount} change{errorCount > 1 ? 's' : ''} NOT saved</span>
          ) : unsavedCount > 0 ? (
            <span className="text-amber-600">Saving…</span>
          ) : (
            <span className="text-green-700 flex items-center gap-1"><Check className="w-3.5 h-3.5" /> All item changes saved</span>
          )}
        </div>
        {errorCount > 0 && (
          <Button size="sm" onClick={retryAll} className="bg-red-600 hover:bg-red-700 text-white h-8 text-xs">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Retry
          </Button>
        )}
      </div>

      {!locked && (
        <div className="border rounded-md p-2 space-y-2 bg-gray-50">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              className="pl-8 h-9 text-sm bg-white"
              placeholder="Search items to add…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          {searching && <p className="text-xs text-gray-500">Searching…</p>}
          {searchResults.length > 0 && (
            <div className="border rounded-md overflow-hidden bg-white max-h-40 overflow-y-auto">
              {searchResults.map(item => (
                <button key={item.name} type="button" onClick={() => selectItem(item)}
                  className="w-full text-left px-2.5 py-2 text-xs hover:bg-blue-50 border-b last:border-0">
                  <p className="font-medium text-gray-900">{item.item_name}</p>
                  <p className="text-[10px] text-gray-500">{item.item_group} · {item.name}</p>
                </button>
              ))}
            </div>
          )}
          {selectedItem && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2 space-y-2">
              <p className="font-semibold text-blue-900 text-sm">{selectedItem.item_name}</p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] font-medium text-gray-600">Zone</label>
                  <Input type="text" placeholder="Zone" value={zone} onChange={e => setZone(e.target.value)} className="h-8 text-xs bg-white" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-gray-600">Rate ($)</label>
                  <Input type="number" min="0" step="0.01" value={rate} onChange={e => setRate(e.target.value)} className="h-8 text-xs bg-white" />
                </div>
                <div>
                  <label className="text-[10px] font-medium text-gray-600">Qty</label>
                  <Input type="number" min="0.01" step="any" value={qty} onChange={e => setQty(e.target.value)} className="h-8 text-xs bg-white" />
                </div>
              </div>
              <textarea
                placeholder="Description (optional)"
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={2}
                className="w-full text-xs border rounded-md p-1.5 text-gray-700 bg-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <Button onClick={addItem} size="sm" className="w-full bg-blue-600 hover:bg-blue-700 h-8 text-xs">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Item to Estimate
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
